import type { AsyncThunk } from '@reduxjs/toolkit';
import { getBaseUrl, request } from 'src/helpers/thunkApi';

interface ThunkRejectValue {
  actionType?: string;
  status?: number;
  [key: string]: unknown;
}

type RequestThunk = AsyncThunk<unknown, unknown, { rejectValue: ThunkRejectValue }>;

export const fetchJobDetails = (jobId?: string, signal?: AbortSignal): RequestThunk => request(
  {
    method: 'GET',
    url: `${getBaseUrl()}/api/service-request/job/${jobId}`,
    contentType: 'json',
    signal,
  },
  'job-details',
);
export const fetchServiceRequestDetails = (
  jobId?: string,
  signal?: AbortSignal,
): RequestThunk => request(
  {
    method: 'GET',
    url: `${getBaseUrl()}/api/service-request/${jobId}`,
    contentType: 'json',
    signal,
  },
  'service-request',
);

export const fetchWorkOrderFlags = (
  jobId?: string | number,
  signal?: AbortSignal,
): RequestThunk => request(
  {
    method: 'GET',
    url: `${getBaseUrl()}/api/flags/work-order/${jobId}`,
    contentType: 'json',
    signal,
  },
  'work-order-flags',
);

export const fetchJobActivityLog = (
  serviceRequestId?: string,
  signal?: AbortSignal,
): RequestThunk => request({
  method: 'GET',
  url: `${getBaseUrl()}/api/service-request/${serviceRequestId}/activity-logs`,
  contentType: 'json',
  signal,
}, 'activity-logs');

// PEG-597 — two-endpoint design for tenant contacts and messages.
export const fetchTenantContacts = (
  serviceRequestId?: string,
  signal?: AbortSignal,
): RequestThunk => request({
  method: 'GET',
  url: `${getBaseUrl()}/api/service-request/${serviceRequestId}/tenant-contacts`,
  contentType: 'json',
  signal,
}, 'tenant-contacts');

export const fetchTenantContactMessages = (
  serviceRequestId?: string,
  contactId?: number,
  signal?: AbortSignal,
): RequestThunk => request({
  method: 'GET',
  url: `${getBaseUrl()}/api/service-request/${serviceRequestId}/tenant_contact/${contactId}`,
  contentType: 'json',
  signal,
}, 'tenant-contact-messages');

export const getJobNotes = (jobId?: string): RequestThunk => request(
  {
    method: 'GET',
    url: `${getBaseUrl()}/api/service-request/job/${jobId}/customer_notes`,
    contentType: 'json',
  },
  'job_customer_notes',
);

export const getServiceRequestNotes = (jobId?: string): RequestThunk => request(
  {
    method: 'GET',
    url: `${getBaseUrl()}/api/service-request/${jobId}/customer_notes`,
    contentType: 'json',
  },
  'service-request-customer-notes',
);

export const getInvoices = (): RequestThunk => request(
  {
    method: 'GET',
    url: `${getBaseUrl()}/api/service-request/invoices`,
    contentType: 'json',
  },
  'invoices',
);

export const getInvoiceHeader = (): RequestThunk => request(
  {
    method: 'GET',
    url: `${getBaseUrl()}/api/service-request/invoices/headers`,
    contentType: 'json',
  },
  'invoice-header',
);

export const getAllPhotos = (jobId?: string, type?: string): RequestThunk => request(
  {
    method: 'GET',
    url: `${getBaseUrl()}/api/service-request/job/${jobId}/media/${type}/download`,
    contentType: 'json',
  },
  `${type}-photos-download`,
);

export const fetchMessages = (
  serviceRequestId?: number,
  chatId?: string,
  signal?: AbortSignal,
): RequestThunk => request(
  {
    method: 'GET',
    url: `${getBaseUrl()}/api/service-request/${serviceRequestId}/group/${chatId}/chats`,
    contentType: 'json',
    signal,
  },
  'fetchMessages',
);

export const sendMessage = (
  serviceRequestId?: number,
  chatId?: string,
  signal?: AbortSignal,
): RequestThunk => request(
  {
    method: 'POST',
    url: `${getBaseUrl()}/api/service-request/${serviceRequestId}/group/${chatId}/chats`,
    contentType: 'json',
    signal,
    shouldAddSocketId: true,
  },
  'sendMessage',
);

export const uploadAttachment = (
  serviceRequestId?: string,
  chatId?: string,
  attachmentId?: string,
  signal?: AbortSignal,
): RequestThunk => request(
  {
    method: 'POST',
    url: `${getBaseUrl()}/api/service-request/${serviceRequestId}/group/${chatId}/chats/${attachmentId}/upload`,
    contentType: 'form-data',
    signal,
    shouldAddSocketId: true,
  },
  'uploadAttachment',
);

export const fetchMessageThreads = (
  serviceRequestId?: number,
  signal?: AbortSignal,
): RequestThunk => request(
  {
    method: 'GET',
    url: `${getBaseUrl()}/api/service-request/${serviceRequestId}/group/list`,
    contentType: 'json',
    signal,
  },
  'fetchMessageThreads',
);

export const markAsReadAndUnreadAction = (
  jobId?: string,
  chatId?: number,
  action?: string,
  signal?: AbortSignal,
): RequestThunk => request(
  {
    method: 'POST',
    url: `${getBaseUrl()}/api/service-request/${jobId}/chats/${chatId}/${action}`,
    contentType: 'json',
    signal,
    shouldAddSocketId: true,
  },
  'markAsReadAndUnreadAction',
);

export const getMaintenanceHistory = (srNo?: string): RequestThunk => request(
  {
    method: 'GET',
    url: `${getBaseUrl()}/api/service-request/maintenance-history/${srNo}`,
    contentType: 'json',
  },
  'maintenance-history',
);

export const uploadCustomerChatAttachment = (
  serviceRequestId?: number,
  chatId?: string,
  signal?: AbortSignal,
): RequestThunk => request(
  {
    method: 'POST',
    url: `${getBaseUrl()}/api/service-request/${serviceRequestId}/group/${chatId}/chats/attachment`,
    contentType: 'form-data',
    signal,
    shouldAddSocketId: true,
  },
  'uploadCustomerChatAttachment',
);

export const updateWatcherSettings = (settingsId?: string): RequestThunk => request({
  method: 'POST',
  url: `${getBaseUrl()}/api/profile/watching-setting/${settingsId}/toggle`,
  contentType: 'json',
}, 'updateWatcherSettings');

export const subscribeToMRWatcher = (serviceRequestId?: number): RequestThunk => request({
  method: 'POST',
  url: `${getBaseUrl()}/api/service-request/${serviceRequestId}/watchers/add`,
  contentType: 'json',
}, 'subscribeToMRWatcher');

export const unsubscribeFromMRWatcher = (serviceRequestId?: number): RequestThunk => request({
  method: 'DELETE',
  url: `${getBaseUrl()}/api/service-request/${serviceRequestId}/watchers/remove`,
  contentType: 'json',
}, 'unsubscribeFromMRWatcher');

export const fetchLumiConversation = (lumiRecordId: number): RequestThunk => request(
  {
    method: 'GET',
    url: `${getBaseUrl()}/api/lumi/conversations/service-request/${lumiRecordId}`,
    contentType: 'json',
  },
  'fetchLumiConversation',
);

export const markResidentResponsibility = (jobId?: number): RequestThunk => request(
  {
    method: 'POST',
    url: `${getBaseUrl()}/api/jobs/${jobId}/resident-caused`,
    contentType: 'json',
  },
  'markResidentResponsibility',
);

export const updateResidentResponsibility = (jobId?: number): RequestThunk => request(
  {
    method: 'PUT',
    url: `${getBaseUrl()}/api/jobs/${jobId}/resident-caused`,
    contentType: 'json',
  },
  'updateResidentResponsibility',
);

export const deleteResidentResponsibility = (jobId?: number): RequestThunk => request(
  {
    method: 'DELETE',
    url: `${getBaseUrl()}/api/jobs/${jobId}/resident-caused`,
    contentType: 'json',
  },
  'deleteResidentResponsibility',
  {jobId}
);
